import { colors } from '../constants';
import { AT_RISK_HOURS_THRESHOLD } from '../constants/slaDashboard';
import { getPriorityColor } from './allQueriesUtils';

/**
 * Parse date string with validation
 * @param {string|Date|null} dateString - Date string or Date object
 * @returns {Date|null} Parsed date or null
 */
export const parseDate = (dateString) => {
  if (!dateString) return null;
  if (dateString instanceof Date) return dateString;
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
};

/**
 * Check if date is valid
 * @param {Date} date - Date object
 * @returns {boolean} True if valid
 */
export const isValidDate = (date) => {
  return date && !isNaN(date.getTime());
};

/**
 * Calculate hours difference between two dates
 * @param {Date} date1 - First date
 * @param {Date} date2 - Second date
 * @returns {number} Hours difference
 */
export const calculateHoursDifference = (date1, date2) => {
  if (!isValidDate(date1) || !isValidDate(date2)) return null;
  return (date2.getTime() - date1.getTime()) / (1000 * 60 * 60);
};

/**
 * Calculate SLA status for a query
 * @param {Date} due - SLA due date
 * @param {Date|null} completedAt - Completion date (respondedAt or closedAt)
 * @param {Date} now - Current date
 * @param {number} atRiskThreshold - Hours threshold for at-risk status
 * @returns {string} Status: 'Within SLA', 'At Risk', or 'Breached'
 */
export const calculateSLAStatus = (due, completedAt, now, atRiskThreshold = AT_RISK_HOURS_THRESHOLD) => {
  if (!isValidDate(due)) return 'Within SLA';
  
  const nowTime = now.getTime();
  const dueTime = due.getTime();
  const hoursUntilDue = (dueTime - nowTime) / (1000 * 60 * 60);

  // Breached: not completed and past due, OR completed after due date
  if ((!completedAt && dueTime < nowTime) || (completedAt && completedAt.getTime() > dueTime)) {
    return 'Breached';
  } else if (!completedAt && dueTime >= nowTime && hoursUntilDue <= atRiskThreshold) {
    // At Risk: not completed, due date is in future but within threshold hours
    return 'At Risk';
  } else {
    // Within SLA: completed on or before due date, OR not completed and due date is more than threshold hours away
    return 'Within SLA';
  }
};

/**
 * Format time remaining
 * @param {number} hoursUntilDue - Hours until due date
 * @returns {string} Formatted time remaining
 */
export const formatTimeRemaining = (hoursUntilDue) => {
  if (hoursUntilDue < 0) {
    return 'Overdue';
  }
  return `${Math.round(hoursUntilDue)} hours`;
};

/**
 * Calculate response time in hours
 * @param {Date} respondedAt - Response date
 * @param {Date} raisedAt - Raised date
 * @returns {number|null} Response time in hours or null
 */
export const calculateResponseTime = (respondedAt, raisedAt) => {
  if (!isValidDate(respondedAt) || !isValidDate(raisedAt)) return null;
  const hours = calculateHoursDifference(raisedAt, respondedAt);
  return hours >= 0 ? hours : null;
};

/**
 * Format response time
 * @param {Date|null} respondedAt - Response date
 * @param {Date|null} raisedAt - Raised date
 * @returns {string} Formatted response time
 */
export const formatResponseTime = (respondedAt, raisedAt) => {
  const hours = calculateResponseTime(respondedAt, raisedAt);
  if (hours === null) return 'N/A';
  return `${hours.toFixed(1)} hours`;
};

/**
 * Calculate resolution time in hours
 * @param {Date} closedAt - Closed date
 * @param {Date} raisedAt - Raised date
 * @returns {number|null} Resolution time in hours or null
 */
export const calculateResolutionTime = (closedAt, raisedAt) => {
  if (!isValidDate(closedAt) || !isValidDate(raisedAt)) return null;
  const hours = calculateHoursDifference(raisedAt, closedAt);
  return hours >= 0 ? hours : null;
};

/**
 * Format priority string
 * @param {string} priority - Priority value
 * @returns {string} Formatted priority
 */
export const formatPriority = (priority) => {
  if (!priority) return 'Unknown';
  return priority.charAt(0).toUpperCase() + priority.slice(1);
};

/**
 * Get status color
 * @param {string} status - SLA status
 * @returns {string} Color hex code
 */
export const getStatusColor = (status) => {
  switch (status) {
    case 'Within SLA':
      return colors.success;
    case 'At Risk':
      return colors.warning;
    case 'Breached':
      return colors.error;
    default:
      return colors.textSecondary;
  }
};

// Re-export getPriorityColor from allQueriesUtils
export { getPriorityColor };
